import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { resolveEntitySlug } from "@/lib/slug";
import { uploadPublicImage } from "@/lib/upload";
import { usePortalUserContext } from "@/admin/PortalUserContext";
import { SocialLinksFormFields } from "@/components/admin/SocialLinksFormFields";
import { AdminImagePicker } from "@/components/admin/AdminImagePicker";
import { adminListRowHighlightClass } from "@/lib/adminListRowHighlight";
import {
  adminAddNewSecondaryButtonClass,
  adminFormSectionClass,
  adminListSectionClass,
  adminScopedFormSectionClass,
} from "@/lib/adminSectionChrome";
import { socialUrlOrNull } from "@/lib/entitySocialLinks";
import { portalHasSiteWideWrite } from "@/lib/portalSession";

type Row = {
  id: string;
  slug: string;
  name: string;
  since_year: number | null;
  description: string | null;
  members_photo_url: string | null;
  social_facebook_url: string | null;
  social_instagram_url: string | null;
  social_youtube_url: string | null;
  social_website_url: string | null;
  social_other_url: string | null;
};

export function BranchesTab() {
  const portal = usePortalUserContext();
  const siteWide = portal ? portalHasSiteWideWrite(portal.grants) : false;
  const branchWorkspaces = useMemo(
    () => portal?.assignments.filter((a) => a.kind === "branch") ?? [],
    [portal?.assignments]
  );
  const activeBranchWorkspace = useMemo(() => {
    if (!portal || branchWorkspaces.length === 0) return null;
    const hit = branchWorkspaces.find((a) => a.id === portal.activeAssignmentId);
    return hit ?? branchWorkspaces[0];
  }, [portal, branchWorkspaces]);
  const scopeBranchId = activeBranchWorkspace?.parent_id ?? null;

  const [rows, setRows] = useState<Row[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [sinceYear, setSinceYear] = useState("");
  const [description, setDescription] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [usernameUrl, setUsernameUrl] = useState("");
  const [socialFacebook, setSocialFacebook] = useState("");
  const [socialInstagram, setSocialInstagram] = useState("");
  const [socialYoutube, setSocialYoutube] = useState("");
  const [socialWebsite, setSocialWebsite] = useState("");
  const [socialOther, setSocialOther] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    let q = supabase.from("branches").select("*");
    if (scopeBranchId) q = q.eq("id", scopeBranchId);
    const { data } = await q.order("name");
    setRows((data as Row[]) ?? []);
  }, [scopeBranchId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (siteWide || rows.length !== 1) return;
    startEdit(rows[0]);
    // Intentionally only when scoped user receives their single row
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteWide, rows]);

  function startNew() {
    setEditingId(null);
    setName("");
    setSinceYear("");
    setDescription("");
    setPhotoUrl("");
    setFile(null);
    setUsernameUrl("");
    setSocialFacebook("");
    setSocialInstagram("");
    setSocialYoutube("");
    setSocialWebsite("");
    setSocialOther("");
    setMsg("");
  }

  function startEdit(r: Row) {
    setEditingId(r.id);
    setName(r.name);
    setSinceYear(r.since_year != null ? String(r.since_year) : "");
    setDescription(r.description ?? "");
    setPhotoUrl(r.members_photo_url ?? "");
    setFile(null);
    setUsernameUrl(r.slug);
    setSocialFacebook(r.social_facebook_url ?? "");
    setSocialInstagram(r.social_instagram_url ?? "");
    setSocialYoutube(r.social_youtube_url ?? "");
    setSocialWebsite(r.social_website_url ?? "");
    setSocialOther(r.social_other_url ?? "");
    setMsg("");
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setMsg("");
    try {
      let url = photoUrl.trim();
      if (file) url = await uploadPublicImage(file, "branches");
      const nameTrim = name.trim();
      if (!nameTrim) {
        setMsg("Name is required.");
        return;
      }
      const finalSlug = resolveEntitySlug(usernameUrl, nameTrim, "branch");
      const payload = {
        slug: finalSlug,
        name: nameTrim,
        since_year: sinceYear ? parseInt(sinceYear, 10) : null,
        description: description || null,
        members_photo_url: url || null,
        social_facebook_url: socialUrlOrNull(socialFacebook),
        social_instagram_url: socialUrlOrNull(socialInstagram),
        social_youtube_url: socialUrlOrNull(socialYoutube),
        social_website_url: socialUrlOrNull(socialWebsite),
        social_other_url: socialUrlOrNull(socialOther),
      };
      if (editingId) {
        const { error } = await supabase
          .from("branches")
          .update(payload)
          .eq("id", editingId);
        if (error) throw error;
        setMsg("Updated.");
      } else {
        if (!siteWide) {
          setMsg("Only full admins can create branches.");
          return;
        }
        const { error } = await supabase.from("branches").insert(payload);
        if (error) throw error;
        setMsg("Created.");
      }
      setFile(null);
      void load();
      startNew();
    } catch (err) {
      console.error(err);
      setMsg("Error saving (check Username / URL is unique).");
    }
  }

  async function onDelete(id: string) {
    if (!siteWide) return;
    if (!confirm("Delete branch and its projects?")) return;
    const { error } = await supabase.from("branches").delete().eq("id", id);
    if (error) console.error(error);
    void load();
    if (editingId === id) startNew();
  }

  const branchForm = (
    <form onSubmit={onSave} className="mt-5 space-y-4">
      {scopeBranchId && !siteWide ? (
        <p className="text-xs text-brand-blue/70">
          You can update this branch&apos;s content. Slug changes may still be restricted by your role at the
          database level.
        </p>
      ) : null}
        <div className="space-y-1">
          <label htmlFor="branch-name" className="block text-xs font-semibold text-brand-blue">
            Name
          </label>
          <input
            id="branch-name"
            required
            autoComplete="off"
            placeholder="Branch name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded border border-brand-gray px-3 py-2 text-sm"
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="branch-since-year" className="block text-xs font-semibold text-brand-blue">
            Since year
          </label>
          <input
            id="branch-since-year"
            inputMode="numeric"
            placeholder="e.g. 1990"
            value={sinceYear}
            onChange={(e) => setSinceYear(e.target.value)}
            className="w-full rounded border border-brand-gray px-3 py-2 text-sm"
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="branch-description" className="block text-xs font-semibold text-brand-blue">
            Description
          </label>
          <textarea
            id="branch-description"
            placeholder="About this branch"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded border border-brand-gray px-3 py-2 text-sm"
          />
        </div>
        <AdminImagePicker
          label="Members photo"
          inputId="branch-photo"
          remoteUrl={photoUrl}
          selectedFile={file}
          onFileChange={setFile}
          onClear={() => {
            setFile(null);
            setPhotoUrl("");
          }}
        />
        <SocialLinksFormFields
          fieldIdPrefix="branch"
          facebookUrl={socialFacebook}
          instagramUrl={socialInstagram}
          youtubeUrl={socialYoutube}
          websiteUrl={socialWebsite}
          otherUrl={socialOther}
          onFacebookUrl={setSocialFacebook}
          onInstagramUrl={setSocialInstagram}
          onYoutubeUrl={setSocialYoutube}
          onWebsiteUrl={setSocialWebsite}
          onOtherUrl={setSocialOther}
        />
        <div className="space-y-1">
          <label htmlFor="branch-username-url" className="block text-xs font-semibold text-brand-blue">
            Username / URL <span className="font-normal text-brand-blue/60">(optional)</span>
          </label>
          <input
            id="branch-username-url"
            autoComplete="off"
            placeholder="Leave blank to auto-generate from name"
            value={usernameUrl}
            onChange={(e) => setUsernameUrl(e.target.value)}
            className="w-full rounded border border-brand-gray px-3 py-2 text-sm"
          />
          <p className="text-xs text-brand-blue/60">
            Public URL segment (e.g. <span className="font-mono">/alumni/branches/uae</span>). When
            editing, this starts as the current value; clear it to regenerate from the name, or set a
            custom segment.
          </p>
        </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!editingId && !siteWide}
          className="rounded-full bg-brand-maroon px-6 py-2 text-sm font-bold uppercase text-white disabled:opacity-50"
        >
          Save
        </button>
        <button
          type="button"
          onClick={startNew}
          className="rounded-full border border-brand-gray px-4 py-2 text-sm"
        >
          Clear
        </button>
      </div>
      {msg && <p className="text-sm">{msg}</p>}
    </form>
  );

  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-brand-blue">Branches</h2>

      {siteWide ? (
        <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-start">
          <section aria-labelledby="branches-list-heading" className={adminListSectionClass}>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-brand-gray pb-3">
              <h3 id="branches-list-heading" className="font-display text-lg font-bold text-brand-blue">
                Branch list
              </h3>
              <button
                type="button"
                onClick={() => {
                  if (editingId) startNew();
                }}
                disabled={!editingId}
                title={
                  !editingId
                    ? "You are already adding a new branch — use the form on the right"
                    : "Clear the editor and add a new branch"
                }
                className={adminAddNewSecondaryButtonClass}
              >
                Add new branch
              </button>
            </div>
            <div className="mt-4 max-h-[min(70vh,36rem)] overflow-auto rounded-lg border border-brand-gray/80 bg-brand-gray/30 p-2">
              <ul className="space-y-1">
                {rows.map((r) => {
                  const selected = editingId === r.id;
                  return (
                    <li
                      key={r.id}
                      className={`flex items-center justify-between gap-2 rounded-lg px-3 py-3 transition-colors ${adminListRowHighlightClass(selected)}`}
                    >
                      <span className="min-w-0 flex-1 font-medium text-brand-blue">{r.name}</span>
                      <div className="flex shrink-0 flex-row items-center gap-4">
                        <button
                          type="button"
                          className="text-xs font-bold uppercase tracking-wide text-brand-maroon"
                          onClick={() => startEdit(r)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="text-xs font-bold uppercase tracking-wide text-red-700"
                          onClick={() => void onDelete(r.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </section>
          <section aria-labelledby="branches-form-heading" className={adminFormSectionClass}>
            <div className="border-b border-brand-gray pb-3">
              <h3 id="branches-form-heading" className="font-display text-lg font-bold text-brand-blue">
                {editingId ? "Edit branch" : "Add new branch"}
              </h3>
              {editingId && name.trim() ? (
                <p className="mt-1 text-sm text-brand-blue/65 line-clamp-2">&ldquo;{name.trim()}&rdquo;</p>
              ) : null}
            </div>
            {branchForm}
          </section>
        </div>
      ) : (
        <>
          <section aria-labelledby="branches-form-heading" className={adminScopedFormSectionClass}>
            <div className="border-b border-brand-gray pb-3">
              <h3 id="branches-form-heading" className="font-display text-lg font-bold text-brand-blue">
                {scopeBranchId ? "Your branch" : editingId ? "Edit branch" : "Add new branch"}
              </h3>
              {editingId && name.trim() ? (
                <p className="mt-1 text-sm text-brand-blue/65 line-clamp-2">&ldquo;{name.trim()}&rdquo;</p>
              ) : null}
            </div>
            {branchForm}
          </section>
          {scopeBranchId && rows[0] ? (
            <p className="mt-6 text-sm text-brand-blue/80">
              Use the form above to save changes to <strong>{rows[0].name}</strong>.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
